import { Injectable } from '@angular/core';
import { collectionCount } from '@marmon/frontend/firebase/firestore';
import {
	Fetchable,
	PaginatedItem,
	PaginatedObject,
} from '@marmon/frontend/pagination';
import { Activity } from '@marmon/shared/types/activity';
import { Entity } from '@marmon/shared/types/common';
import { User } from '@marmon/shared/types/user';
import { sanitize } from '@marmon/shared/utils';
import {
	collection,
	doc,
	DocumentReference,
	DocumentSnapshot,
	getDocs,
	limit,
	query,
	setDoc,
	startAfter,
	Timestamp,
} from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ActivityService implements Fetchable<Activity> {
	private static readonly CollectionId = 'activity';

	create(
		entityRef: DocumentReference<Pick<Entity, 'id'>>,
		activity: Pick<Activity, 'type' | 'data'>,
		creator: DocumentReference<User>,
	): Promise<Activity> {
		const { type, data } = activity;
		const now = Timestamp.now();
		const payload: Activity = {
			type,
			user: creator,
			createdAt: now,
			updatedAt: now,
			data,
			id: crypto.randomUUID(),
		};

		// the activity collection tied to the entity
		const activityCollection = collection(
			entityRef,
			ActivityService.CollectionId,
		);

		return setDoc(
			doc(activityCollection, payload.id),
			sanitize(payload),
		).then(() => payload);
	}

	async fetch(
		paginated?: PaginatedObject<Activity>,
	): Promise<PaginatedObject<Activity>> {
		if (!paginated) paginated = new PaginatedObject<Activity>();
		if (!paginated?.parentRef) throw new Error('Parent ref is required');

		const activityCollection = collection(
			paginated.parentRef,
			ActivityService.CollectionId,
		);

		if (!paginated.totalElements) {
			paginated.totalElements = await firstValueFrom(
				collectionCount(
					query(activityCollection, ...paginated.constraints),
				),
			);
		}

		const constraints = [...paginated.constraints];
		const lastDoc = paginated.last ?? null;

		if (lastDoc) constraints.push(startAfter(lastDoc));
		if (paginated.pageSize) constraints.push(limit(paginated.pageSize));
		const snapshot = await getDocs(
			query(activityCollection, ...constraints),
		);

		let last: DocumentSnapshot | null = null;
		const items = snapshot.docs.map((d) => {
			last = d;
			return {
				id: d.id,
				ref: d.ref,
				data: d.data() as Activity,
			} as PaginatedItem<Activity>;
		});

		paginated.update({ last });
		paginated.add(items);

		return paginated;
	}
}
